import React, { useState, useCallback, useMemo } from "react";
import Header from "../../../Components/Header/Header";
import CouponReport from "./CouponReport";
import { COUPON_REPORT_TYPE } from "Helpers/Constants";
import GlobalDateFilters from "Components/GlobalDateFilters/GlobalDateFilters";
import moment from "moment";

const CouponReportPage = () => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  /**
   * UI State for filter inputs (not yet applied)
   * These values update as the user interacts with the filter controls
   */
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState("By Month");
  const [selectedPeriod, setSelectedPeriod] = useState("thisMonth");
  
  const [fromDate, setFromDate] = useState(
    moment().startOf("month").format("ddd MMM DD, YYYY")
  );
  const [toDate, setToDate] = useState(
    moment().endOf("month").format("ddd MMM DD, YYYY")
  );
  const [checkAllStore, setCheckAllStore] = useState(true);

  /**
   * Applied Filters State
   * This state only updates when user clicks Search or Clear button.
   * Child components watch this to trigger data fetching.
   * Using useMemo to ensure stable reference when values haven't changed.
   */
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    fromDate: moment().startOf("month").format("YYYY-MM-DD"),
    toDate: moment().endOf("month").format("YYYY-MM-DD"),
    selectedPeriod: "thisMonth",
    // Add a timestamp to force re-fetch even if dates are the same
    timestamp: Date.now()
  }));

  // ============================================================================
  // FILTER CONFIGURATION
  // ============================================================================
  
  /** Period filter options for the dropdown */
  const selectedPeriodOptions = useMemo(() => [
    { id: "thisMonth", title: "This Month", label: "This Month" },
    { id: "lastMonth", title: "Last Month", label: "Last Month" },
    { id: "thisYear", title: "This Year", label: "This Year" },
    { id: "lastYear", title: "Last Year", label: "Last Year" },
    { id: "custom", title: "Custom", label: "Custom" },
  ], []);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Applies current filter values to trigger data refresh in child components.
   * Called when user clicks the Search button.
   */
  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      selectedPeriod,
      // Timestamp ensures a new reference even if dates are the same
      timestamp: Date.now()
    });
  }, [fromDate, toDate, selectedPeriod]);

  /**
   * Resets all filters to default values (current month).
   * Called when user clicks the Clear button.
   * @param {Event} event - Click event from the clear button
   */
  const handleClearSearch = useCallback((event) => {
    event.preventDefault();
    
    // Reset UI state to defaults
    const startDate = moment().startOf("month").format("ddd MMM DD, YYYY");
    const endDate = moment().endOf("month").format("ddd MMM DD, YYYY");
    
    setFromDate(startDate);
    setToDate(endDate);
    setSelectedPeriod("thisMonth");
    setCheckAllStore(true);

    // Apply the reset filters immediately
    setAppliedFilters({
      fromDate: moment().startOf("month").format("YYYY-MM-DD"),
      toDate: moment().endOf("month").format("YYYY-MM-DD"),
      selectedPeriod: "thisMonth",
      timestamp: Date.now()
    });
  }, []);

  /**
   * Handles checkbox change for "All Stores" option
   * @param {boolean} isChecked - Whether the checkbox is checked
   */
  const handleCheckAllStoreChange = useCallback((isChecked) => {
    setCheckAllStore(isChecked);
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <React.Fragment>
      <Header title="Coupon Report" />
      <div id="MainContent" className="main-content">
        {/* Global Date Filter Controls */}
        <div className="col-sm-12 col-xs-12 m-0 p-0">
          <GlobalDateFilters
            isDateFilter
            isSearchIcon
            isClearSearchBtn
            isAllCheckStoreBnt
            isFilterByPeriod
            isSelectedPeriodLabel
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            fetchData={handleApplyFilters}
            handleClearSearch={handleClearSearch}
            checkAllStore={handleCheckAllStoreChange}
            defaultSetCheckAllStore={checkAllStore}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            periodOptions={selectedPeriodOptions}
            selectedPeriodLabel={selectedPeriodLabel}
            setSelectedPeriodLabel={setSelectedPeriodLabel}
            key="coupon-report"
          />
        </div>

        {/* Global Coupon Report Section */}
        <h3 className="bold">Global Coupon</h3>
        <CouponReport 
          type={COUPON_REPORT_TYPE.GLOBAL} 
          appliedFilters={appliedFilters}
          checkAllStore={checkAllStore}
        />

        {/* Local Coupon Report Section */}
        <h3 className="bold">Local Coupon</h3>
        <CouponReport 
          type={COUPON_REPORT_TYPE.LOCAL} 
          appliedFilters={appliedFilters}
          checkAllStore={checkAllStore}
        />
      </div>
    </React.Fragment>
  );
};

export default CouponReportPage;
